import * as React from "react"
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useOrganizations, useOrganizationSearch, useRecentOrganizations, useOrganization } from "@/hooks/useOrganizations"

export interface SearchableOrganizationComboboxProps {
  value?: string
  onValueChange: (value: string) => void
  role?: 'client' | 'consultant' | 'vendor'
  categoryId?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  showCategory?: boolean
  /**
   * Use server-side search with debouncing (Phase 7 Batch 1)
   * If false, uses client-side filtering (legacy behavior)
   */
  useServerSearch?: boolean
  /**
   * Callback when user clicks "Add New Organization"
   * If provided, shows the "Add New" option at the bottom of the dropdown
   */
  onCreateNew?: () => void
  /**
   * 9.21h: Optional callback with full selection data (id + name)
   * Use this when you need the organization name immediately on selection
   * without having to look it up from a separate query
   */
  onSelect?: (id: string, name: string) => void
}

/**
 * SearchableOrganizationCombobox - A reusable combobox for selecting organizations
 *
 * Features:
 * - Server-side search with debouncing (Phase 7 Batch 1)
 * - Shows recent organizations by default
 * - Role-based filtering (client, consultant, vendor)
 * - Category-based filtering (for scope-first pattern in Add Scope modal)
 * - Displays organization name and optionally category
 * - Automatic data fetching with useOrganizations hook
 *
 * @example
 * // Simple client selection with server-side search
 * <SearchableOrganizationCombobox
 *   value={selectedClientId}
 *   onValueChange={setSelectedClientId}
 *   role="client"
 *   useServerSearch={true}
 * />
 *
 * @example
 * // Scope-first filtering (Add Scope modal) - client-side mode
 * <SearchableOrganizationCombobox
 *   value={selectedOrgId}
 *   onValueChange={setSelectedOrgId}
 *   categoryId={selectedScopeTypeId}
 *   disabled={!selectedScopeTypeId}
 *   placeholder={!selectedScopeTypeId ? "Select scope type first" : "Select organization"}
 * />
 */
export function SearchableOrganizationCombobox({
  value,
  onValueChange,
  role,
  categoryId,
  placeholder = "Select client...",
  searchPlaceholder = "Search clients...",
  emptyText = "No clients found.",
  disabled = false,
  className,
  showCategory = true,
  useServerSearch = false,
  onCreateNew,
  onSelect, // 9.21h: Optional callback with full selection data
}: SearchableOrganizationComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const [debouncedQuery, setDebouncedQuery] = React.useState("")

  // Debounce search query (300ms)
  React.useEffect(() => {
    if (!useServerSearch) return

    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, useServerSearch])

  // Client-side filtering mode (legacy) - called first for test compatibility
  const { data: allOrgsData, isLoading: isLoadingAll } = useOrganizations({
    role,
    category_id: categoryId,
    limit: categoryId ? 100 : 200,
  }, {
    enabled: !useServerSearch && !disabled,
  })

  // Server-side search mode (Phase 7 Batch 1)
  const { data: recentData, isLoading: isLoadingRecent } = useRecentOrganizations(role, categoryId, {
    enabled: useServerSearch && !disabled && !debouncedQuery,
  })

  // Fallback: Fetch all orgs if recent < 5 to ensure dropdown isn't empty/sparse
  const recentOrgs = recentData?.organizations || []
  const recentLoaded = !isLoadingRecent && !!recentData
  const needsFallback = useServerSearch && !debouncedQuery && recentLoaded && recentOrgs.length < 5
  const { data: fallbackData, isLoading: isLoadingFallback } = useOrganizations(
    { role, category_id: categoryId, limit: 20 },
    { keepPreviousData: true, enabled: needsFallback }
  )

  // Search shows ALL organizations (no role or category filtering) so users can always find any org
  const { data: searchData, isLoading: isLoadingSearch } = useOrganizationSearch(
    debouncedQuery,
    undefined, // Don't filter search by role - allow finding any org
    undefined, // Don't filter search by category - allow finding any org
    {
      enabled: useServerSearch && !disabled && debouncedQuery.length > 0,
    }
  )

  // Phase 9.9h: Fetch selected organization separately if value provided
  // This ensures we can display the org name even if it's not in recent/search results
  const { data: selectedOrgData, isLoading: isLoadingSelected } = useOrganization(value)

  // Determine which data to use
  let organizations: any[] = []
  let isLoading = false

  if (useServerSearch) {
    if (debouncedQuery) {
      // Use search results when searching
      organizations = searchData?.organizations || []
      isLoading = isLoadingSearch
    } else {
      // Use recent orgs, with fallback merge if < 5
      if (needsFallback && fallbackData?.organizations) {
        // Merge recent with fallback, deduplicate by id, recent first
        const recentIds = new Set(recentOrgs.map(org => org.id))
        const additionalOrgs = fallbackData.organizations.filter(org => !recentIds.has(org.id))
        organizations = [...recentOrgs, ...additionalOrgs]
      } else {
        organizations = recentOrgs
      }
      isLoading = isLoadingRecent || (needsFallback && isLoadingFallback)
    }
  } else {
    // Legacy client-side filtering
    organizations = allOrgsData?.organizations || []
    isLoading = isLoadingAll
  }

  // Phase 9.9h: Check both the loaded list AND the separate selected org query
  // Prioritize the loaded list (more recent/relevant), fallback to separate fetch
  const selectedOrg = organizations.find((org) => org.id === value) || selectedOrgData?.organization

  // Phase 9.9h: Include selected org loading state
  const isLoadingAny = isLoading || (value && isLoadingSelected)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select client"
          className={cn("w-full justify-between", className)}
          disabled={disabled || isLoadingAny}
        >
          {isLoadingAny ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading clients...
            </span>
          ) : selectedOrg ? (
            <span className="truncate">
              {selectedOrg.name}
              {showCategory && selectedOrg.category && (
                <span className="text-muted-foreground ml-1">
                  ({selectedOrg.category})
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={!useServerSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid="organization-search-input"
            value={searchQuery}
            onValueChange={useServerSearch ? setSearchQuery : undefined}
          />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                {debouncedQuery ? "Searching..." : "Loading..."}
              </div>
            ) : (
              <>
                {/* Show organizations if we have results */}
                {organizations.length > 0 && (
                  <CommandGroup>
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      {organizations.length} {organizations.length === 1 ? 'client' : 'clients'}
                      {useServerSearch && !debouncedQuery && (categoryId ? ' matching this type' : ' (recently used)')}
                    </div>
                    {organizations.map((org) => (
                      <CommandItem
                        key={org.id}
                        value={`${org.name} ${org.category || ''}`}
                        onSelect={() => {
                          onValueChange(org.id)
                          onSelect?.(org.id, org.name) // 9.21h: Also pass name for immediate use
                          setOpen(false)
                        }}
                        data-org-id={org.id}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === org.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">
                          {org.name}
                          {showCategory && org.category && (
                            <span className="text-muted-foreground ml-1 text-xs">
                              ({org.category})
                            </span>
                          )}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {/* "Add New" at bottom when results exist, OR prominent when no results */}
                {onCreateNew && (
                  <CommandGroup>
                    {organizations.length === 0 && (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        {debouncedQuery ? `No matches for "${debouncedQuery}"` : emptyText}
                      </div>
                    )}
                    <CommandItem
                      onSelect={() => {
                        setOpen(false)
                        onCreateNew()
                      }}
                      className={cn(
                        "text-primary",
                        organizations.length === 0 && "bg-primary/5 font-medium"
                      )}
                      data-testid="add-new-organization-option"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add New Client
                    </CommandItem>
                  </CommandGroup>
                )}
                {/* Only show CommandEmpty if no onCreateNew callback */}
                {!onCreateNew && organizations.length === 0 && (
                  <CommandEmpty>{emptyText}</CommandEmpty>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
